import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { graphql } from "gatsby";
import Slider from "react-slick";
import { GatsbyImage, getImage, withArtDirection } from "gatsby-plugin-image";
import Dropdown from "react-overlays/Dropdown";
import PinIcon from "../icons/pin.svg";
import PinIconMobile from "../icons/pin-mobile.svg";
import classNames from "classnames";
import { Link } from "./link";
import safeSlugify from "../utility/safe-slugify";
import useIsMobile from "../hooks/use-is-mobile";
import { eventInternalLinkClick } from "../utility/datalayer";

const calcPosition = (x, y) => {
  const position =
    x < 50 && y < 50
      ? "top-left"
      : x > 49 && y < 50
      ? "top-right"
      : x < 50 && y > 49
      ? "bottom-left"
      : "bottom-right";
  return position;
};

const hasPinPosition = (xDesktop, yDesktop, xMobile, yMobile) => {
  return (
    !!xDesktop &&
    xDesktop !== "" &&
    !!yDesktop &&
    yDesktop !== "" &&
    !!xMobile &&
    xMobile !== "" &&
    !!yMobile &&
    yMobile !== ""
  );
};

const PinButton = ({ pin, toggleProps, setPinOpen, isOpen, isMobile, pagePath }) => {
  const { onClick, ...otherProps } = toggleProps;

  const timeoutRef = useRef();

  const onClickPinButton = useCallback(
    (value) => {
      eventInternalLinkClick({
        click_element: pagePath === "/" ? "hp_pin_spot" : "lp_pin_spot",
        link_text: pagePath === "/" ? `hp_pin_${value}` : `lp_pin_${value}`,
        slug: pagePath,
      });
    },
    [pagePath]
  );

  return (
    <button
      className="slider-pin__pin"
      style={{
        "--abscissa-desktop": `${pin.horizontalAxisDesktop}%`,
        "--ordinate-desktop": `${pin.verticalAxisDesktop}%`,
        "--abscissa-mobile": `${pin.horizontalAxisMobile}%`,
        "--ordinate-mobile": `${pin.verticalAxisMobile}%`,
      }}
      {...otherProps}
      onClick={(e) => {
        if (isMobile) {
          onClickPinButton(pin?.tile);
        }
        onClick(e);
      }}
      onMouseOver={() => !isMobile && (timeoutRef.current = setTimeout(setPinOpen, 100))}
      onMouseOut={() => clearTimeout(timeoutRef.current)}
      onBlur={() => clearTimeout(timeoutRef.current)}
      onFocus={() => !isMobile && setPinOpen()}
      onMouseEnter={() => !isMobile && onClickPinButton(pin?.title)}
    >
      <PinIcon
        className={classNames(
          "slider-pin__pin-icon d-none d-md-block",
          isOpen && "slider-pin__pin-icon--active"
        )}
      />
      <PinIconMobile
        className={classNames(
          "slider-pin__pin-icon d-block d-md-none",
          isOpen && "slider-pin__pin-icon--active"
        )}
      />
    </button>
  );
};

const Tooltip = ({ pin, pagePath }) => {
  const xDesktop = parseInt(pin?.horizontalAxisDesktop);
  const yDesktop = parseInt(pin?.verticalAxisDesktop);
  const xMobile = parseInt(pin?.horizontalAxisMobile);
  const yMobile = parseInt(pin?.verticalAxisMobile);
  const pinPositionDesktop = calcPosition(xDesktop, yDesktop);
  const pinPositionMobile = calcPosition(xMobile, yMobile);
  const colorArrowDesktop =
    (pin?.title && (pinPositionDesktop === "top-left" || pinPositionDesktop === "top-right")) ||
    (pin?.title && !pin?.description)
      ? "#fff"
      : "#ededed";
  const colorArrowMobile =
    (pin?.title && (pinPositionMobile === "top-left" || pinPositionMobile === "top-right")) ||
    (pin?.title && !pin?.description)
      ? "#fff"
      : "#ededed";

  const onClickPinCard = useCallback(
    (value, url) => {
      eventInternalLinkClick({
        click_element: pagePath === "/" ? "hp_pin_card" : "lp_pin_card",
        link_text: pagePath === "/" ? `hp_pin_${value}` : `lp_pin_${value}`,
        link_url: url,
        slug: pagePath,
      });
    },
    [pagePath]
  );

  return pin?.title || pin?.description ? (
    <div
      className={`slider-pin__tooltip slider-pin__tooltip-${pinPositionDesktop} slider-pin__tooltip-mobile-${pinPositionMobile}`}
      style={{
        "--arrow-color-desktop": colorArrowDesktop,
        "--arrow-color-mobile": colorArrowMobile,
      }}
    >
      {pin?.title && (
        <div className="slider-pin__tooltip-title text-sm-semibold">
          {pin.title}
          {pin?.link && <span className="slider-pin__tooltip-arrow"></span>}
        </div>
      )}
      {pin?.description && (
        <div
          className="slider-pin__tooltip-description text-xs-light"
          dangerouslySetInnerHTML={{
            __html: pin.description,
          }}
        ></div>
      )}
      {pin?.link && pin?.title && (
        <Link
          className="slider-pin__tooltip-link"
          to={pin.link}
          target={pin?.linkTarget?.[0] || "_self"}
          onClick={() => onClickPinCard(pin?.title, pin?.link)}
        />
      )}
    </div>
  ) : null;
};

const PinTab = ({ tab, id, active, pagePath }) => {
  const isMobile = useIsMobile(767);
  const latest = useRef(0);
  const [pinOpen, setPinOpen] = useState(tab?.pin?.findIndex((pin) => !!pin?.defaultOpen) || 0);

  useEffect(() => {
    if (pinOpen !== -1) {
      latest.current = pinOpen;
    }
  }, [pinOpen]);

  useEffect(() => {
    if (active) {
      setPinOpen(latest.current || 0);
    }
  }, [active]);

  return (
    <>
      <GatsbyImage
        style={{
          "--aspect-ratio": tab?.imageMobile?.node?.resize?.aspectRatio,
        }}
        image={tab.images}
        alt={tab.altImage}
      />
      {tab?.pin?.length > 0 &&
        tab.pin.map((pin, i) =>
          hasPinPosition(
            pin?.horizontalAxisDesktop,
            pin?.verticalAxisDesktop,
            pin?.horizontalAxisMobile,
            pin?.verticalAxisMobile
          ) ? (
            <Dropdown
              show={active && pinOpen === i}
              key={id + i}
              onToggle={(nextShow, event) => {
                event.stopPropagation();
                (isMobile || !!event.pointerType) &&
                  setPinOpen((current) => (nextShow ? i : current === i ? -1 : current));
              }}
            >
              <Dropdown.Toggle>
                {(props) => (
                  <PinButton
                    pin={pin}
                    setPinOpen={() => setPinOpen(i)}
                    toggleProps={props}
                    isOpen={active && pinOpen === i}
                    isMobile={isMobile}
                    pagePath={pagePath}
                  />
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {(props) => (
                  <div className="slider-pin__tooltip-wrapper" {...props}>
                    <Tooltip pin={pin} pagePath={pagePath} />
                  </div>
                )}
              </Dropdown.Menu>
            </Dropdown>
          ) : null
        )}
    </>
  );
};

const Pin = ({ data, isFirst, pagePath }) => {
  const [swipe, setSwipe] = useState(true);
  const tabsRef = useRef();

  const updateSwipe = useCallback(() => {
    const list = tabsRef?.current?.innerSlider?.list;
    if (list) {
      const tabs = [...list.querySelectorAll(".slick-slide")];
      let tabsLength = 0;

      for (let index = 0; index < tabs.length; index++) {
        const tabLength = tabs[index].getBoundingClientRect().width;
        tabsLength += tabLength;
      }
      setSwipe(tabsLength >= list.getBoundingClientRect().width + 1);
    }
  }, []);

  const settings = {
    dots: false,
    infinite: false,
    arrows: false,
    slidesToShow: "auto",
    variableWidth: true,
    swipe: swipe,
    onInit: () => setTimeout(updateSwipe, 10),
  };
  const [currentSlide, setCurrentSlide] = useState(0);
  const settingsImage = {
    dots: false,
    infinite: false,
    arrows: false,
    swipe: false,
    slidesToShow: 1,
    fade: true,
    speed: 500,
    beforeChange: (current, next) => setCurrentSlide(next),
  };

  const sliderTabs = useMemo(() => {
    return data?.tab?.map((tab) => ({
      ...tab,
      images: withArtDirection(getImage(tab.imageDesktop.node), [
        {
          media: "(max-width: 767.98px)",
          image: getImage(tab.imageMobile.node),
        },
      ]),
      altImage: tab?.imageDesktop?.value?.description ? tab.imageDesktop.value.description : "",
    }));
  }, [data?.tab]);

  const sliderTabsFiltered = sliderTabs?.filter(
    (tab) =>
      !!tab?.tabName && tab?.tabName !== "" && tab?.imageDesktop?.node && tab?.imageMobile?.node
  );

  const sliderRef = useRef();
  const setSlider = useCallback((slider) => (sliderRef.current = slider), []);

  useEffect(() => {
    window.addEventListener("resize", updateSwipe);
    return () => {
      window.removeEventListener("resize", updateSwipe);
    };
  }, [updateSwipe]);

  const onClickSliderTab = useCallback(
    (value) => {
      eventInternalLinkClick({
        click_element: pagePath === "/" ? "hp_pin_title" : "lp_pin_title",
        link_text: pagePath === "/" ? `hp_pin_${value}` : `lp_pin_${value}`,
        slug: pagePath,
      });
    },
    [pagePath]
  );

  return (
    <>
      {data?.anchorDesktop && (
        <span id={safeSlugify(data.anchorDesktop)} className="tabs__scroll-anchor" />
      )}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            {data?.titlePin && (
              <>
                {isFirst ? (
                  <h1 className="section__title">{data?.titlePin}</h1>
                ) : (
                  <h2 className="section__title">{data?.titlePin}</h2>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Slider ref={tabsRef} {...settings} className="slider-pin__tabs">
        {sliderTabsFiltered?.length > 0 &&
          sliderTabsFiltered.map((tab, index) => (
            <button
              key={index}
              className={classNames(
                "slider-pin__tab",
                currentSlide === index && "slider-pin__tab--active"
              )}
              onClick={() => {
                onClickSliderTab(tab?.tabName);
                sliderRef.current.slickGoTo(index);
              }}
            >
              {tab?.tabName}
            </button>
          ))}
      </Slider>
      <Slider {...settingsImage} className="slider-pin__images" ref={setSlider}>
        {sliderTabsFiltered?.length > 0 &&
          sliderTabsFiltered.map((tab, i) => (
            <PinTab tab={tab} key={i} id={i} active={currentSlide === i} pagePath={pagePath} />
          ))}
      </Slider>
    </>
  );
};

export default Pin;

export const fragment = graphql`
  fragment PinFragment on LiferayPin {
    anchorDesktop
    anchorMobile
    titlePin
    tab {
      tabName
      imageMobile {
        value {
          description
        }
        node {
          publicURL
          gatsbyImageData(layout: FULL_WIDTH)
          resize {
            aspectRatio
            src
            width
            height
          }
        }
      }
      imageDesktop {
        value {
          description
        }
        node {
          publicURL
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      pin {
        title
        defaultOpen
        description
        horizontalAxisDesktop
        horizontalAxisMobile
        link
        linkTarget
        verticalAxisDesktop
        verticalAxisMobile
      }
    }
  }
`;
